import { useApolloClient } from "@apollo/client";
import { useAuthState } from "@package/ui-authentication";
import {
  GetScheduledEventDocument,
  GetScheduledEventsDocument,
} from "@package/ui-gql/index-data-access";
import { useOrganizationState } from "@package/ui-organization";
import axios from "axios";
import { useCallback, useState } from "react";

const { ENV_VAR_API_URI_BASE } = import.meta.env;

/**
 * There is no GraphQL mutation for deleting a recurring group -- it's a
 * REST-only endpoint (scheduled-event.controller.ts), so this wires the
 * auth headers by hand in one place instead of duplicating them at every
 * call site.
 */
export function useDeleteScheduledEventGroup() {
  const { token } = useAuthState();
  const { selectedOrganization } = useOrganizationState();
  const client = useApolloClient();
  const [deleting, setDeleting] = useState(false);

  const deleteGroup = useCallback(
    async (groupId: string) => {
      setDeleting(true);

      try {
        await axios
          .create({ baseURL: ENV_VAR_API_URI_BASE })
          .delete(`/api/scheduled-event/group/${groupId}`, {
            timeout: 60 * 1000,
            headers: {
              Authorization: `Bearer ${token}`,
              "x-org-id": selectedOrganization?.orgId,
            },
          });

        await client.refetchQueries({
          include: [GetScheduledEventDocument, GetScheduledEventsDocument],
        });
      } finally {
        setDeleting(false);
      }
    },
    [client, selectedOrganization?.orgId, token],
  );

  return { deleteGroup, deleting };
}
